import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import {Tag} from '@/components/ui/tag'
import {STATUS_TONE, planAccionHref} from '@/components/accion-ligada-link'
import {EficaciaTag} from '@/components/action-eficacia-block'
import {EvidenciaLigada} from '@/components/descargar-evidencia'
import {NotesGlyph, PencilGlyph, TrashGlyph} from '@/components/ui/icons'
import {InactiveBadge} from '@/components/inactive-badge'
import {MicroLabel} from '@/components/period-selector'
import {TargetDateMeta} from '@/components/target-date-meta'
import type {SgsstAction} from '@/hooks/sgsst/plan/use-actions'
import {formatShortDate, localToday} from '@/lib/risk'
import {formatCopRangeOf, type Cop} from '@/lib/money'
import {cn} from '@/lib/utils'
import {documentoCorto} from '@/lib/documento'

function ResponsibleChip({action}: {action: SgsstAction}) {
  const {t} = useTranslation()
  const employee = action.responsible_employee
  if (!employee) return null
  return (
    <div>
      <MicroLabel>{t('plan.meta.responsible')}</MicroLabel>
      <p className="mt-1 flex flex-wrap items-center gap-1.5">
        <span
          data-testid="responsible-chip"
          className="inline-flex max-w-full items-center truncate rounded-full border border-input px-2.5 py-0.5 text-xs font-medium text-foreground">
          {employee.name} {employee.last_name}
        </span>
        {employee && employee.status === 'inactive' && (
          <InactiveBadge since={employee.deactivated_at} testId="inactive-tag" />
        )}
      </p>
      <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
        {documentoCorto(employee.tipo_documento, employee.cedula)}
      </p>
    </div>
  )
}

// `risk` is null for a transversal action: no standard, so no weight in the Tabla de Valores.
function RiskCell({risk, cumplida}: {risk: Cop | null; cumplida: boolean}) {
  const {t} = useTranslation()
  return (
    <div className="text-right" data-testid="action-risk">
      <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
        {t('plan.risk.label')}
      </p>
      {risk === null ? (
        <>
          <p className="mt-0.5 font-mono text-[13px] text-muted-foreground">—</p>
          <p className="text-[10.5px] text-muted-foreground italic">{t('plan.risk.transversal')}</p>
        </>
      ) : (
        <>
          <p className={cn('mt-0.5 font-mono text-[13px]', cumplida ? 'text-muted-foreground' : 'text-foreground')}>
            {cumplida ? <s>{formatCopRangeOf(risk)}</s> : formatCopRangeOf(risk)}
          </p>
          {cumplida && <p className="text-[10.5px] text-muted-foreground">{t('plan.risk.covered')}</p>}
        </>
      )}
    </div>
  )
}

/** Cada chip lleva al elemento exacto donde nació la acción (D4). */
function OrigenChips({businessId, action}: {businessId: string; action: SgsstAction}) {
  const {t} = useTranslation()
  return (
    <>
      {action.jerarquia &&
        (action.riesgo_fila_id ? (
          <Link
            to={`/businesses/${businessId}/sgsst/matriz-riesgos?fila=${action.riesgo_fila_id}`}
            data-testid="action-riesgo-chip">
            <Tag tone="neutral">{t(`ipevr.jerarquia.${action.jerarquia}`)}</Tag>
          </Link>
        ) : (
          // Sin disparador de ayuda: se repetiría fila a fila (presupuesto de disparadores).
          <Tag tone="neutral">{t(`ipevr.jerarquia.${action.jerarquia}`)}</Tag>
        ))}
      {!action.jerarquia && action.riesgo_fila_id && (
        <Link
          to={`/businesses/${businessId}/sgsst/matriz-riesgos?fila=${action.riesgo_fila_id}`}
          data-testid="action-riesgo-chip">
          <Tag tone="neutral">{t('plan.chip.fila')}</Tag>
        </Link>
      )}
      {action.legal_norma && action.legal_item_id && (
        <Link
          to={`/businesses/${businessId}/sgsst/matriz-legal?item=${action.legal_item_id}`}
          data-testid="action-legal-chip">
          <Tag tone="neutral">{action.legal_norma}</Tag>
        </Link>
      )}
      {action.investigacion_incidente_id && (
        <Link
          to={`/businesses/${businessId}/incidentes/${action.investigacion_incidente_id}/investigacion?paso=medidas&medida=${action.id}`}
          data-testid="action-investigacion-chip">
          <Tag tone="neutral">{t('plan.chip.investigacion')}</Tag>
        </Link>
      )}
      {action.eficacia_de_action_id && (
        <Link
          to={planAccionHref(businessId, action.eficacia_de_action_id)}
          title={action.eficacia_de_descripcion ?? undefined}
          data-testid="action-eficacia-padre-chip">
          <Tag tone="neutral">{t('plan.chip.seguimientoDe')}</Tag>
        </Link>
      )}
    </>
  )
}

export function ActionRow({
  businessId,
  action,
  planYear,
  editable,
  risk,
  standardLabel,
  onEdit,
  onDelete,
  onOpenNotes,
}: {
  businessId: string
  action: SgsstAction
  planYear: number
  editable: boolean
  risk: Cop | null
  /** Only in the flat view, where the action renders outside its standard's group. */
  standardLabel?: {code: string | null; name?: string}
  onEdit: (action: SgsstAction) => void
  onDelete: (action: SgsstAction) => void
  onOpenNotes: (action: SgsstAction) => void
}) {
  const {t} = useTranslation()
  // nosemgrep: browser-clock-business-date -- overdue determination off the browser clock; tracked debt, not fixed in this wave (FE-02)
  const overdue = action.status !== 'cumplida' && action.target_date < localToday()
  return (
    <div
      // `useHighlightParam` trae esta fila a la vista desde `?accion=<id>`.
      data-highlight-id={`accion-${action.id}`}
      className="-mx-4 border-t border-border px-4 py-3 transition-colors first:border-t-0 hover:bg-secondary/30"
      data-testid="plan-action">
      {standardLabel !== undefined && (
        <p
          className="mb-1.5 font-mono text-[10px] tracking-wider text-muted-foreground uppercase"
          data-testid="action-standard-label">
          {standardLabel.code ? (
            <>
              {standardLabel.code}
              {standardLabel.name && <span className="normal-case"> · {standardLabel.name}</span>}
            </>
          ) : (
            t('plan.transversal')
          )}
        </p>
      )}
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-foreground">{action.description}</p>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <OrigenChips businessId={businessId} action={action} />
            <Tag tone={STATUS_TONE[action.status]}>{t(`plan.status.${action.status}`)}</Tag>
            {action.eficacia && <EficaciaTag veredicto={action.eficacia} testId="action-eficacia-tag" />}
            {/* Always reachable, even read-only: Historial reads the frozen thread. */}
            <button
              type="button"
              aria-label={t('nota.open')}
              data-testid="open-action-notes"
              onClick={() => onOpenNotes(action)}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground">
              <NotesGlyph />
            </button>
            {editable && (
              <>
                <button
                  type="button"
                  aria-label={t('plan.edit')}
                  data-testid="edit-action"
                  onClick={() => onEdit(action)}
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground">
                  <PencilGlyph />
                </button>
                <button
                  type="button"
                  aria-label={t('plan.delete')}
                  data-testid="delete-action"
                  onClick={() => onDelete(action)}
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground">
                  <TrashGlyph />
                </button>
              </>
            )}
          </div>
          <RiskCell risk={risk} cumplida={action.status === 'cumplida'} />
        </div>
      </div>
      <div className="mt-2 grid gap-3 text-sm sm:grid-cols-3">
        <ResponsibleChip action={action} />
        <div>
          <MicroLabel>{t('plan.meta.resources')}</MicroLabel>
          <p className="mt-1 text-sm text-muted-foreground">{action.resources ?? '—'}</p>
        </div>
        <TargetDateMeta
          targetDate={action.target_date}
          previousTargetDate={action.previous_target_date ?? null}
          overdue={overdue}
        />
      </div>
      {action.status === 'cumplida' && action.completed_at && (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="completed-line">
          <span className="font-mono">
            {t('plan.completedOn', {date: formatShortDate(action.completed_at.slice(0, 10))})}
          </span>
          {action.soporte && (
            <>
              {' · '}
              {t('plan.soporte')}{' '}
              <EvidenciaLigada businessId={businessId} evidencia={action.soporte} testId="soporte-link" />
            </>
          )}
        </p>
      )}
      {action.carried_from_action_id && (
        <p className="mt-1 font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
          {t('plan.carried', {year: planYear - 1})}
        </p>
      )}
    </div>
  )
}
